/* eslint-disable react-hooks/rules-of-hooks */
import classNames from 'classnames'
import type { FC } from 'react'
import React from 'react'

import type { TagProps } from './types'
import type { Theme } from '../../theme-provider/types'
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import { borderWidths } from '../../themes/theme-keys'
import { getSafeReadableColor } from '../../utils/colors'
import { EMPTY_OBJECT, get } from '../../utils/object'
import { omitPaddingX, omitPaddingY } from '../../utils/props-utils'
import { reactNodeToString } from '../../utils/react-helpers'
import { getStepSize, resolveSize } from '../../utils/size-utils'
import { FlexInlinePrivate } from '../_primitives/flex/flex-inline'
import { TextPrivate } from '../_primitives/text/text'
import Button from '../button/button'
import type { ButtonIconProps } from '../button/types'

const closeIconProps: ButtonIconProps = { icon: 'close', strokeWidth: 2.5 }

const Tag: FC<TagProps> = React.forwardRef<
  HTMLDivElement,
  TagProps & { theme?: Theme }
>(function Tag(props, ref) {
  const theme = useSafeThemeUI(props)
  const {
    variant,
    className,
    onRemove,
    size = 'xs',
    px = 'xs',
    gap = 'xs',
    useThemeVariant = !!variant,
    outline,
    color,
    children,
    border,
    prefixItem,
    postfixItem,
    disabled,
    textProps = EMPTY_OBJECT,
    sx = EMPTY_OBJECT,
    ...rest
  } = props

  //const _fontSize = resolveSize(size, get(theme, 'sizes.fontSizes'))
  const _IconSize = resolveSize(getStepSize(size, -4), get(theme, 'sizes.icon'))
  const defaultTextColor = get(theme, 'colors.text', '#000')
  const defaultWhite = get(theme, 'colors.white', '#fff')
  const _color = color
    ? get(
        theme,
        `colors.${color}.${THEME_UI_DEFAULT_KEY}`,
        get(theme, `colors.${color}`, '#000')
      )
    : defaultTextColor

  const content = (
    <FlexInlinePrivate
      justifyContent="center"
      alignItems="center"
      minWidth="0px"
      gap="xxs"
    >
      {prefixItem && <FlexInlinePrivate>{prefixItem}</FlexInlinePrivate>}
      <TextPrivate
        as="div"
        alignItems="center"
        ellipsis
        {...textProps}
        postContent={reactNodeToString(postfixItem)}
      >
        {children}
      </TextPrivate>
      {postfixItem && <FlexInlinePrivate>{postfixItem}</FlexInlinePrivate>}
    </FlexInlinePrivate>
  )

  return (
    <FlexInlinePrivate
      ref={ref}
      variant={useThemeVariant ? variant ?? THEME_UI_DEFAULT_KEY : undefined}
      __themeKey="tags"
      className={classNames('csg-tui-tag', className, {
        'is-disabled': !!disabled
      })}
      alignItems="center"
      //alignItems={prefixItem || postfixItem ? 'center' : 'baseline'}
      pl={prefixItem ? 'xs-2' : undefined}
      justifyContent="center"
      minWidth="0px"
      lineHeight="1.4" // Cosmos does not  have 1.4, but required
      //fontSize={_fontSize}
      gap={onRemove ? gap : undefined}
      {...omitPaddingX(omitPaddingY(rest))}
      __css={{
        ...(useThemeVariant
          ? {
              ...(onRemove ? { pl: px } : { px })
            }
          : outline
            ? {
                border: border
                  ? border
                  : `${borderWidths.default} solid ${_color}`,
                bg: get(theme, `colors.${color}.light`, defaultWhite),
                color: get(theme, `colors.${color}.dark`, _color),
                px
              }
            : {
                bg: _color,
                border: border ? border : 'transparent',
                color: `${getSafeReadableColor(
                  _color,
                  defaultTextColor,
                  defaultWhite
                )}`,
                px
              })
      }}
      sx={{
        '&.is-disabled': {
          opacity: 0.75
        },
        ...omitPaddingX(omitPaddingY(sx as object))
      }}
    >
      {content}
      {onRemove && (
        <Button
          variant="icon"
          icon={closeIconProps}
          color="inherit"
          disabled={disabled}
          tabIndex={-1}
          p="0"
          size={_IconSize}
          onClick={onRemove}
        />
      )}
    </FlexInlinePrivate>
  )
})

export default Tag
